import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  useGetRealtimeVehiclesQuery,
  useGetRouteDetailsQuery,
} from '#/services/developerApi';
import { errorThrown } from '#/shared/slices/error';
import { updateVehicleRoute } from '#/transit-tracker/queries';
import { vehicleRouteCleared } from '#/transit-tracker/slice';

const useSingleRealtimeVehicle = (
  routeId,
  blockId,
  { pollingInterval = null } = {}
) => {
  const {
    vehicle,
    isLoading: vehicleLoading,
    isError: vehicleHasError,
    refetch: refetchVehicle,
    fulfilledTimeStamp: vehicleTimestamp,
  } = useGetRealtimeVehiclesQuery(
    {
      routes: routeId,
      onRouteOnly: true,
    },
    {
      pollingInterval,
      selectFromResult: result => ({
        ...result,
        vehicle: result?.data?.find(
          v => v.blockId === blockId || v.tripId === blockId
        ),
      }),
    }
  );

  const dispatch = useDispatch();

  if (vehicleHasError) {
    // TODO: raise less jarring error to user here as this puts on overlay
    //  over the entire screen
    dispatch(errorThrown());
  }

  const { data: route } = useGetRouteDetailsQuery({
    route: routeId,
    tp: true,
  });

  const { directionId, lastLocId, nextLocId, vehicleId } = vehicle || {};

  // get the upcoming stops and arrivals for the vehicle
  useEffect(() => {
    if (!vehicleId) {
      dispatch(vehicleRouteCleared());
    } else if (pollingInterval) {
      // if multiple instances of this hook are being used, only one should
      // pass a truthy `pollingInterval` to prevent duplicate arrivals calls

      dispatch(
        updateVehicleRoute(route, vehicleId, directionId, lastLocId, nextLocId)
      );
    }
  }, [
    pollingInterval,
    route,
    vehicleId,
    directionId,
    lastLocId,
    nextLocId,
    dispatch,

    // this triggers the useEffect to update arrivals each time the
    // vehicle is updated
    vehicleTimestamp,
  ]);

  useEffect(() => () => dispatch(vehicleRouteCleared()), [dispatch]);

  const { vehicleRoute, vehicleRouteTimestamp } = useSelector(
    s => s.transitTracker
  );

  return {
    vehicle,
    vehicleLoading,
    vehicleNotFound: !vehicleHasError && !vehicleLoading && !vehicle,
    refetchVehicle,
    vehicleRoute,
    vehicleRouteTimestamp,
  };
};

export default useSingleRealtimeVehicle;
